import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock, Landmark, Plus, Smartphone, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import BottomNav from "../components/BottomNav";
import { queueDashboardNotice } from "../utils/appInteractions";

const UPI_WITHDRAW_LIMIT = 2000;

function Withdraw() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [method, setMethod] = useState("bank");
  const [form, setForm] = useState({ amount: "", bank_name: "", account_holder: "", account_number: "", ifsc: "", upi_id: "", save_bank: true });
  const [savedBanks, setSavedBanks] = useState([]);
  const [savedUpis, setSavedUpis] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState("");
  const [selectedUpiId, setSelectedUpiId] = useState("");
  const [showBankForm, setShowBankForm] = useState(false);
  const [showUpiForm, setShowUpiForm] = useState(false);
  const [wallet, setWallet] = useState({ winnings: 0, balance: 0 });
  const [settings, setSettings] = useState({ min_withdrawal: "500", max_withdrawal: "99999" });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [successAmt, setSuccessAmt] = useState(null);

  useEffect(() => {
    if (!user?.id) { navigate("/login"); return; }

    Promise.all([
      API.get(`/bank-accounts/${user.id}`).catch(() => ({ data: [] })),
      API.get(`/wallet/${user.id}`).catch(() => ({ data: { balance: 0 } })),
      API.get("/settings/public").catch(() => ({ data: {} })),
    ]).then(([bankRes, walletRes, settingsRes]) => {
      const rows = Array.isArray(bankRes.data) ? bankRes.data : [];
      const banks = rows.filter((a) => a.account_number && !String(a.account_number).startsWith("UPI-"));
      const upis  = rows.filter((a) => String(a.account_number || "").startsWith("UPI-"));
      setSavedBanks(banks);
      setSavedUpis(upis);
      setShowBankForm(banks.length === 0);
      setShowUpiForm(upis.length === 0);
      if (banks[0]) applyBank(banks[0]);
      if (upis[0]) applyUpi(upis[0]);
      setWallet(walletRes.data || { balance: 0 });
      setSettings((c) => ({ ...c, ...(settingsRes.data || {}) }));
      setLoaded(true);
    });
  }, [navigate, user]);

  const amount = Number(form.amount || 0);
  const minWithdrawal = Number(settings.min_withdrawal || 500);
  const maxWithdrawal = Number(settings.max_withdrawal || 99999);
  const upiBlocked = method === "upi" && amount > UPI_WITHDRAW_LIMIT;
  const hasBankDetails = method === "bank" ? savedBanks.length > 0 || showBankForm : savedUpis.length > 0 || showUpiForm;

  const updateField = (field, value) => setForm((c) => ({ ...c, [field]: value }));

  const applyBank = (account) => {
    setSelectedBankId(String(account.id || ""));
    setForm((c) => ({ ...c, bank_name: account.bank_name || "", account_holder: account.account_holder || "", account_number: account.account_number || "", ifsc: account.ifsc || "" }));
  };

  const applyUpi = (account) => {
    setSelectedUpiId(String(account.id || ""));
    setForm((c) => ({ ...c, upi_id: String(account.account_number || "").replace(/^UPI-/i, "") }));
  };

  const handleWithdraw = async () => {
    if (!user?.id) return alert("Please login first");
    if (!amount) return alert("Enter amount");
    if (amount < minWithdrawal) return alert(`Minimum withdrawal is ₹${minWithdrawal}`);
    if (amount > maxWithdrawal) return alert(`Maximum withdrawal is ₹${maxWithdrawal}`);
    if (upiBlocked) return alert(`UPI withdrawal is available only up to ₹${UPI_WITHDRAW_LIMIT}. Please select Bank.`);

    const payload = { user_id: user.id, amount, save_bank: form.save_bank };
    if (method === "upi") {
      if (!form.upi_id) return alert("Enter UPI ID");
      Object.assign(payload, { bank_name: "UPI", account_holder: user?.name || user?.phone || "UPI User", account_number: `UPI-${form.upi_id}`, ifsc: "UPI" });
    } else {
      if (!form.bank_name || !form.account_holder || !form.account_number || !form.ifsc) return alert("Enter complete bank details");
      Object.assign(payload, { bank_name: form.bank_name, account_holder: form.account_holder, account_number: form.account_number, ifsc: form.ifsc });
    }

    setBusy(true);
    try {
      await API.post("/request-withdraw", payload);
      setSuccessAmt(amount);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to send withdraw request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={page}>
      <h2 style={pageTitle}>Withdraw Fund</h2>

      {/* Balance card */}
      <div style={balCard}>
        <span style={balIcon}><Landmark size={20} color="#ea580c" /></span>
        <div>
          <div style={balAmount}>₹{Number(wallet.balance || 0).toFixed(0)}</div>
          <div style={balLabel}>Total Wallet Balance</div>
        </div>
      </div>

      {/* Method tabs */}
      <div style={methodTabs}>
        <button type="button" style={method === "bank" ? activeTab : tabBtn} onClick={() => setMethod("bank")}>
          <Building2 size={16} /> Bank
        </button>
        <button type="button" style={method === "upi" ? activeTab : tabBtn} onClick={() => setMethod("upi")}>
          <Smartphone size={16} /> UPI
        </button>
      </div>

      {/* Amount input */}
      <div style={fieldGroup}>
        <label style={fieldLabel}>Amount*</label>
        <input
          style={amtInput}
          type="number"
          inputMode="numeric"
          placeholder={`Enter amount (min: ₹${minWithdrawal})`}
          value={form.amount}
          onChange={(e) => updateField("amount", e.target.value)}
        />
      </div>

      {upiBlocked && <div style={warnBox}>UPI withdrawal is only allowed up to ₹{UPI_WITHDRAW_LIMIT}. Select Bank for higher amounts.</div>}

      {/* Bank / UPI details */}
      {loaded && (
        method === "bank" ? (
          <div style={detailBox}>
            <div style={detailHead}>
              <strong style={detailTitle}>Bank Details</strong>
              <button type="button" style={addBtn} onClick={() => { setSelectedBankId(""); setShowBankForm(true); setForm((c) => ({ ...c, bank_name: "", account_holder: "", account_number: "", ifsc: "" })); }}><Plus size={16} /></button>
            </div>
            {savedBanks.length > 0 && (
              <div style={savedList}>
                {savedBanks.map((a) => (
                  <button key={a.id} type="button" style={String(a.id) === selectedBankId ? savedActive : savedItem} onClick={() => { applyBank(a); setShowBankForm(false); }}>
                    <div>
                      <div style={savedName}>{a.bank_name}</div>
                      <div style={savedSub}>•••• {String(a.account_number || "").slice(-4)}</div>
                    </div>
                    {String(a.id) === selectedBankId && <CheckCircle2 size={18} color="#ea580c" />}
                  </button>
                ))}
              </div>
            )}
            {showBankForm && (
              <div style={formGrid}>
                <input style={fieldInput} placeholder="Bank name" value={form.bank_name} onChange={(e) => updateField("bank_name", e.target.value)} />
                <input style={fieldInput} placeholder="Account holder name" value={form.account_holder} onChange={(e) => updateField("account_holder", e.target.value)} />
                <input style={fieldInput} placeholder="Account number" value={form.account_number} onChange={(e) => updateField("account_number", e.target.value)} />
                <input style={fieldInput} placeholder="IFSC code" value={form.ifsc} onChange={(e) => updateField("ifsc", e.target.value.toUpperCase())} />
              </div>
            )}
          </div>
        ) : (
          <div style={detailBox}>
            <div style={detailHead}>
              <strong style={detailTitle}>UPI Details</strong>
              <button type="button" style={addBtn} onClick={() => { setSelectedUpiId(""); setShowUpiForm(true); setForm((c) => ({ ...c, upi_id: "" })); }}><Plus size={16} /></button>
            </div>
            {savedUpis.length > 0 && (
              <div style={savedList}>
                {savedUpis.map((a) => (
                  <button key={a.id} type="button" style={String(a.id) === selectedUpiId ? savedActive : savedItem} onClick={() => { applyUpi(a); setShowUpiForm(false); }}>
                    <div>
                      <div style={savedName}>UPI</div>
                      <div style={savedSub}>{String(a.account_number || "").replace(/^UPI-/i, "")}</div>
                    </div>
                    {String(a.id) === selectedUpiId && <CheckCircle2 size={18} color="#ea580c" />}
                  </button>
                ))}
              </div>
            )}
            {showUpiForm && <input style={fieldInput} placeholder="example@upi" value={form.upi_id} onChange={(e) => updateField("upi_id", e.target.value.trim())} />}
          </div>
        )
      )}

      <label style={checkRow}>
        <input type="checkbox" checked={form.save_bank} onChange={(e) => updateField("save_bank", e.target.checked)} />
        Save this detail for next withdrawal
      </label>

      {/* Action button */}
      {!loaded || (!hasBankDetails && !showBankForm && !showUpiForm) ? (
        <button type="button" style={submitBtn} onClick={() => navigate("/bank-accounts")}>
          Update Bank Details
        </button>
      ) : (
        <button type="button" style={upiBlocked || busy ? { ...submitBtn, background: "#94a3b8", boxShadow: "none" } : submitBtn} onClick={handleWithdraw} disabled={upiBlocked || busy}>
          {busy ? "Submitting..." : "Submit Withdraw Request"}
        </button>
      )}

      {/* Notes */}
      <div style={notesBox}>
        <div style={noteRow}>Withdrawal limit is ₹{minWithdrawal} to ₹{maxWithdrawal}</div>
        <div style={noteRow}>Withdrawal Request timing is 11:00 AM to 11:00 PM</div>
      </div>

      <BottomNav />

      {/* Withdraw success popup */}
      {successAmt !== null && (
        <div style={successOverlay}>
          <div style={successCard}>
            <div style={successIconWrap}>
              <CheckCircle2 size={48} color="#16a34a" strokeWidth={1.8} />
            </div>
            <h2 style={successTitle}>Withdrawal Submitted!</h2>
            <div style={successAmount}>₹{Number(successAmt).toFixed(0)}</div>
            <div style={successBadgeRow}>
              <span style={successBadge}>
                <Clock size={13} />
                Payment Under Process
              </span>
            </div>
            <p style={successMsg}>
              Your withdrawal request has been received. The amount will be credited to your account after admin approval.
            </p>
            <button type="button" style={successOkBtn} onClick={() => { setSuccessAmt(null); queueDashboardNotice(`Withdraw request for ₹${successAmt} submitted.`); navigate("/dashboard", { replace: true }); }}>
              Go to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const page      = { minHeight: "100vh", background: "#f4f6fb", padding: "20px 14px 100px", boxSizing: "border-box", fontFamily: "Inter, system-ui, sans-serif" };
const pageTitle = { textAlign: "center", fontSize: 22, fontWeight: 900, color: "#0f172a", margin: "0 0 18px" };

const balCard   = { display: "flex", alignItems: "center", gap: 14, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: "16px 18px", marginBottom: 16, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" };
const balIcon   = { width: 44, height: 44, borderRadius: 12, background: "#fff7ed", display: "grid", placeItems: "center", flexShrink: 0 };
const balAmount = { fontSize: 22, fontWeight: 900, color: "#ea580c" };
const balLabel  = { fontSize: 12, color: "#64748b", fontWeight: 600, marginTop: 2 };

const methodTabs = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 };
const tabBtn     = { height: 42, border: "1.5px solid #e2e8f0", borderRadius: 12, background: "#fff", color: "#64748b", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 14, cursor: "pointer" };
const activeTab  = { ...tabBtn, background: "#7c2d12", color: "#fff", borderColor: "#7c2d12", boxShadow: "0 4px 14px rgba(49,46,129,0.3)" };

const fieldGroup = { marginBottom: 12 };
const fieldLabel = { display: "block", marginBottom: 6, fontSize: 13, fontWeight: 800, color: "#374151" };
const amtInput   = { width: "100%", height: 48, padding: "0 14px", border: "1.5px solid #d1d5db", borderRadius: 12, boxSizing: "border-box", fontSize: 15, fontWeight: 700, color: "#0f172a", outline: "none", background: "#fff" };
const warnBox    = { marginBottom: 12, padding: "10px 14px", borderRadius: 12, background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa", fontSize: 12, fontWeight: 700 };

const detailBox  = { background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 16, padding: "14px", marginBottom: 12 };
const detailHead = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 };
const detailTitle= { fontSize: 14, fontWeight: 900, color: "#0f172a" };
const addBtn     = { width: 30, height: 30, border: 0, borderRadius: "50%", background: "#431407", color: "#fff", display: "grid", placeItems: "center", cursor: "pointer" };
const savedList  = { display: "grid", gap: 8, marginBottom: 10 };
const savedItem  = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", border: "1.5px solid #e2e8f0", borderRadius: 12, background: "#f8fafc", cursor: "pointer", textAlign: "left" };
const savedActive= { ...savedItem, border: "1.5px solid #ea580c", background: "#fff7ed" };
const savedName  = { fontSize: 13, fontWeight: 800, color: "#431407" };
const savedSub   = { fontSize: 12, color: "#64748b", fontWeight: 600, marginTop: 2 };
const formGrid   = { display: "grid", gap: 8 };
const fieldInput = { width: "100%", height: 44, padding: "0 12px", border: "1.5px solid #d1d5db", borderRadius: 10, boxSizing: "border-box", fontSize: 14, fontWeight: 600, outline: "none", background: "#fff" };
const checkRow   = { display: "flex", alignItems: "center", gap: 8, color: "#374151", fontWeight: 700, fontSize: 12, margin: "0 0 16px", cursor: "pointer" };
const submitBtn  = { width: "100%", height: 50, border: 0, borderRadius: 14, background: "#7c2d12", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", boxShadow: "0 8px 24px rgba(49,46,129,0.28)", marginBottom: 16 };
const notesBox   = { display: "grid", gap: 6 };
const noteRow    = { fontSize: 12, color: "#64748b", fontWeight: 600, paddingLeft: 14, position: "relative" };

const successOverlay = { position: "fixed", inset: 0, zIndex: 200, background: "rgba(10,14,30,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const successCard = { width: "min(380px, 100%)", background: "#fff", borderRadius: 24, padding: "32px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, boxShadow: "0 24px 64px rgba(0,0,0,0.28)", textAlign: "center" };
const successIconWrap = { width: 80, height: 80, borderRadius: "50%", background: "#f0fdf4", border: "2px solid #bbf7d0", display: "grid", placeItems: "center" };
const successTitle = { margin: 0, fontSize: 22, fontWeight: 900, color: "#0f172a" };
const successAmount = { fontSize: 32, fontWeight: 900, color: "#16a34a" };
const successBadgeRow = { display: "flex", justifyContent: "center" };
const successBadge = { display: "inline-flex", alignItems: "center", gap: 5, background: "#fef9c3", color: "#854d0e", border: "1px solid #fde68a", borderRadius: 999, padding: "5px 14px", fontSize: 12, fontWeight: 800 };
const successMsg = { margin: 0, fontSize: 13, color: "#64748b", lineHeight: 1.6, maxWidth: 300 };
const successOkBtn = { width: "100%", height: 50, border: 0, borderRadius: 14, background: "linear-gradient(135deg, #431407, #ea580c)", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", marginTop: 4, boxShadow: "0 8px 24px rgba(49,46,129,0.28)" };

export default Withdraw;
